import { parseLineString } from '@/services/lineStringService'

const COORD_EPSILON = 1e-9

const areCoordsEqual = (a: [number, number], b: [number, number]) =>
  Math.abs(a[0] - b[0]) <= COORD_EPSILON && Math.abs(a[1] - b[1]) <= COORD_EPSILON

const projectPointOntoSegment = (
  point: [number, number],
  start: [number, number],
  end: [number, number],
) => {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  if (dx === 0 && dy === 0) {
    const distSq = (point[0] - start[0]) ** 2 + (point[1] - start[1]) ** 2
    return { projected: start, distSq }
  }

  const t = Math.max(
    0,
    Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / (dx * dx + dy * dy)),
  )
  const projected: [number, number] = [start[0] + dx * t, start[1] + dy * t]
  const distSq = (point[0] - projected[0]) ** 2 + (point[1] - projected[1]) ** 2
  return { projected, distSq }
}

export type TrackLineInsertionProjection = {
  projected: [number, number]
  segmentStart: [number, number]
  segmentEnd: [number, number]
  /** Polyline vertices from start up to and including `segmentStart` (no projected point appended). */
  beforeVertices: [number, number][]
  /** Polyline vertices from `segmentEnd` to end (no projected point prepended). */
  afterVertices: [number, number][]
  /** Same as beforeVertices, with the projected point appended (unless it coincides with the last vertex). */
  beforeCoords: [number, number][]
  /** Same as afterVertices, with the projected point prepended (unless it coincides with the first vertex). */
  afterCoords: [number, number][]
}

export const getTrackLineInsertionProjection = (
  lineString: string | undefined,
  lat: number,
  lon: number,
): TrackLineInsertionProjection | null => {
  if (!lineString) return null

  const polyline = parseLineString(lineString)
  if (polyline.length < 2) return null

  let bestSegmentIndex = -1
  let bestProjected: [number, number] | null = null
  let bestDistSq = Number.POSITIVE_INFINITY
  const target: [number, number] = [lon, lat]

  for (let i = 1; i < polyline.length; i++) {
    const candidate = projectPointOntoSegment(target, polyline[i - 1], polyline[i])
    if (candidate.distSq < bestDistSq) {
      bestDistSq = candidate.distSq
      bestSegmentIndex = i - 1
      bestProjected = candidate.projected
    }
  }

  if (bestSegmentIndex < 0 || !bestProjected) return null

  const segmentStart = polyline[bestSegmentIndex]
  const segmentEnd = polyline[bestSegmentIndex + 1]
  const beforeVertices = polyline.slice(0, bestSegmentIndex + 1)
  const afterVertices = polyline.slice(bestSegmentIndex + 1)

  const beforeCoords = beforeVertices.slice()
  const afterCoords = afterVertices.slice()

  if (!areCoordsEqual(beforeCoords[beforeCoords.length - 1], bestProjected)) {
    beforeCoords.push(bestProjected)
  }
  if (afterCoords.length === 0 || !areCoordsEqual(afterCoords[0], bestProjected)) {
    afterCoords.unshift(bestProjected)
  }

  return {
    projected: bestProjected,
    segmentStart,
    segmentEnd,
    beforeVertices,
    afterVertices,
    beforeCoords,
    afterCoords,
  }
}
